import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, MenuItem, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useAuth } from '../../providers/AuthProvider';
import { attendanceService, AttendanceStatus, CourseSession, RollCall } from '../../services/attendanceService';
import { tdGroupService, TDGroup } from '../../services/tdGroupService';
import StudentPhoto from '../../components/StudentPhoto/StudentPhoto';
import TrombiRollCall from './TrombiRollCall';
import { formatSessionDate, formatSessionTime, sessionTitle, STATUS_META, STATUS_ORDER, teacherNames } from './attendanceUi';

const VIEW_KEY = 'pwa-dashboard.rollCallView';

function readView(): 'list' | 'trombi' {
  try {
    return localStorage.getItem(VIEW_KEY) === 'trombi' ? 'trombi' : 'list';
  } catch {
    return 'list';
  }
}

const hasStarted = (s: CourseSession, now: Date) => new Date(`${s.date}T${s.start_time || '00:00'}`) <= now;

// Saisie de l'appel : un·e enseignant·e saisit les séances qu'il/elle encadre, l'admin toutes
const RollCallTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const { getValidToken } = useAuth();
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [onlyMine, setOnlyMine] = useState(!isAdmin);
  const [tdGroups, setTdGroups] = useState<TDGroup[]>([]);
  // '' = tous les groupes, 'promo' = séances de toute la promo (CM, CCS), sinon code du groupe TD
  const [tdFilter, setTdFilter] = useState('');
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [roll, setRoll] = useState<RollCall | null>(null);
  const [statuses, setStatuses] = useState<Record<number, AttendanceStatus | null>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'trombi'>(readView);

  const changeView = (value: 'list' | 'trombi' | null) => {
    if (!value) return;
    setView(value);
    try {
      localStorage.setItem(VIEW_KEY, value);
    } catch {
      // stockage indisponible : le choix vaut pour cette page seulement
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const token = await getValidToken();
        if (!token) throw new Error('Session expirée, reconnectez-vous.');
        const list = await attendanceService.listSessions(token);
        setSessions(list);
        // Par défaut : la dernière séance commencée que l'on peut saisir
        const now = new Date();
        const started = list.filter(s => s.can_record && hasStarted(s, now));
        if (started.length) setSessionId(started[started.length - 1].id);
        setTdGroups(await tdGroupService.list(token));
      } catch (e: any) {
        setError(e.message || 'Erreur lors du chargement des séances');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recharge l'appel depuis le serveur (saisies du / de la co-encadrant·e comprises)
  const loadRoll = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const data = await attendanceService.getRollCall(id, token);
      setRoll(data);
      setStatuses(Object.fromEntries(data.students.map(s => [s.id, s.status])));
    } catch (e: any) {
      setError(e.message || "Erreur lors du chargement de l'appel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    setMessage(null);
    loadRoll(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const visibleSessions = sessions.filter(s => (!onlyMine || s.can_record)
    && (tdFilter === '' || (tdFilter === 'promo' ? s.td_group === null : s.td_group === tdFilter)));

  // Changement de filtre : si la séance affichée n'en fait plus partie, on passe à la dernière
  // séance commencée de la sélection (ou à la première à venir)
  useEffect(() => {
    if (sessionId === '' && sessions.length === 0) return;
    if (visibleSessions.some(s => s.id === sessionId)) return;
    const now = new Date();
    const started = visibleSessions.filter(s => hasStarted(s, now));
    const next = started.length ? started[started.length - 1] : visibleSessions[0];
    setSessionId(next ? next.id : '');
    if (!next) setRoll(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tdFilter, onlyMine, sessions]);

  const canRecord = !!roll?.session.can_record;
  const dirty = !!roll && roll.students.some(s => (statuses[s.id] ?? null) !== s.status);

  const handleSave = async () => {
    if (!roll) return;
    setSaving(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      // Seulement les lignes modifiées ici : en TP, les deux encadrant·es peuvent faire l'appel en
      // même temps sans effacer ce que l'autre a saisi (sur un·e même étudiant·e, la dernière saisie l'emporte)
      const records = roll.students
        .filter(s => (statuses[s.id] ?? null) !== s.status)
        .map(s => ({ user_id: s.id, status: statuses[s.id] ?? null }));
      const result = await attendanceService.saveRollCall(roll.session.id, records, token);
      await loadRoll(roll.session.id);
      setMessage(result.message);
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Relit d'abord l'appel sur le serveur : un·e étudiant·e déjà saisi·e par le / la co-encadrant·e
  // ne doit pas repasser « présent·e ». Les modifications locales non enregistrées sont gardées.
  const markUnsetAsPresent = async () => {
    if (!roll) return;
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const fresh = await attendanceService.getRollCall(roll.session.id, token);
      const localEdits = Object.fromEntries(roll.students
        .filter(s => (statuses[s.id] ?? null) !== s.status)
        .map(s => [s.id, statuses[s.id] ?? null]));
      setRoll(fresh);
      setStatuses(Object.fromEntries(fresh.students.map(s => [
        s.id, (s.id in localEdits ? localEdits[s.id] : s.status) ?? 'present',
      ])));
    } catch (e: any) {
      setError(e.message || "Erreur lors du chargement de l'appel");
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField select size="small" label="Groupe TD" value={tdFilter} sx={{ minWidth: 160 }}
          onChange={(e) => setTdFilter(e.target.value)}>
          <MenuItem value="">Tous les groupes</MenuItem>
          <MenuItem value="promo">Promo entière</MenuItem>
          {tdGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Séance" value={sessionId} sx={{ minWidth: 420 }}
          onChange={(e) => setSessionId(Number(e.target.value))}>
          {visibleSessions.map(s => (
            <MenuItem key={s.id} value={s.id}>
              {formatSessionDate(s.date)} {s.start_time || ''} — {sessionTitle(s)}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel control={<Switch checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />}
          label={isAdmin ? 'Seulement les séances que j\'encadre' : 'Seulement mes séances'} />
        {loading && <CircularProgress size={24} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {visibleSessions.length === 0 && !error && (
        <Alert severity="info">Aucune séance {onlyMine ? 'qui vous est attribuée' : 'planifiée'}{tdFilter ? ' pour ce groupe' : ''}.</Alert>
      )}

      {roll && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">{sessionTitle(roll.session)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {formatSessionDate(roll.session.date)} {formatSessionTime(roll.session)} · {roll.session.room || 'salle non précisée'} · {teacherNames(roll.session)}
          </Typography>
          {!canRecord && (
            <Alert severity="info" sx={{ mb: 2 }}>Consultation seule : vous n'encadrez pas cette séance.</Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, value) => changeView(value)}>
              <ToggleButton value="list">Liste</ToggleButton>
              <ToggleButton value="trombi">Trombi</ToggleButton>
            </ToggleButtonGroup>
          {canRecord && (
            <>
              <Button variant="outlined" onClick={markUnsetAsPresent}>Non saisi·es → présent·es</Button>
              <Button variant="contained" disabled={!dirty || saving} onClick={handleSave}>
                {saving ? <CircularProgress size={20} /> : 'Enregistrer'}
              </Button>
              <Button disabled={loading || dirty} onClick={() => loadRoll(roll.session.id)}
                title={dirty ? 'Enregistrez d\'abord vos modifications' : 'Afficher les saisies du / de la co-encadrant·e'}>
                Actualiser
              </Button>
            </>
          )}
          </Box>
          {view === 'trombi' ? (
            <TrombiRollCall students={roll.students} statuses={statuses} canRecord={canRecord} isAdmin={isAdmin}
              onChange={(userId, status) => setStatuses({ ...statuses, [userId]: status })} />
          ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Étudiant·e</TableCell>
                  <TableCell>Groupe</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roll.students.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <StudentPhoto userId={s.id} lastname={s.lastname} firstname={s.firstname} />
                        {s.lastname} {s.firstname}
                      </Box>
                    </TableCell>
                    <TableCell>{s.td_group}</TableCell>
                    <TableCell>
                      {/* Absence justifiée : posée et modifiée par l'admin seulement (contrôlé aussi par l'API) */}
                      {!isAdmin && s.status === 'excused' ? (
                        <Chip size="small" color={STATUS_META.excused.color} label={`${STATUS_META.excused.label} (admin)`} />
                      ) : (
                        <ToggleButtonGroup size="small" exclusive disabled={!canRecord} value={statuses[s.id] ?? null}
                          onChange={(_, value) => setStatuses({ ...statuses, [s.id]: value })}>
                          {STATUS_ORDER.filter(status => isAdmin || status !== 'excused').map(status => (
                            <ToggleButton key={status} value={status} color={STATUS_META[status].color}>
                              {STATUS_META[status].label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {roll.students.length === 0 && (
                  <TableRow><TableCell colSpan={3}>Aucun·e étudiant·e dans ce groupe.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      )}
    </>
  );
};

export default RollCallTab;
